import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useApp } from '../lib/app-context';
import GoogleButton from '../components/GoogleButton';
import { clearReferral, getReferral } from '../lib/referral';
import { Field, Notice, Select, TextInput } from '../components/ui';
import type { AuthResponse } from '../lib/types';
import { language, t } from '../lib/i18n';

/**
 * Login, signup and password reset.
 *
 * Email is the verified identity: signup sends a 6-digit code to it, and that
 * code is what unlocks booking and publishing. A phone number is still asked
 * for, as a contact for the other side of a booking, but it is optional and
 * nothing sends it a code.
 *
 * Signup is two steps because the account exists after the first one: the
 * server returns tokens straight away and the address stays unverified, so a
 * guest whose email is slow to arrive is not stuck with a half-created account —
 * they can browse and verify later.
 *
 * In development the API echoes the code as `devCode`; it is shown here so the
 * flow can be walked without a mail server. The env schema refuses that setting
 * in production, so this notice cannot appear for real users.
 */

type Mode = 'login' | 'signup' | 'reset';

/**
 * Login takes either identifier in one field. An @ means an email; anything
 * else is treated as a phone number and normalized by the server. One field
 * rather than two tabs, because people do not remember which one they signed
 * up with — and the answer should not matter.
 */
const identifierPayload = (value: string): { email: string } | { phone: string } =>
  value.includes('@') ? { email: value.trim().toLowerCase() } : { phone: value.trim() };

export default function Auth({ mode }: { mode: Mode }) {
  const { signIn, toast, reportError } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/compte';

  const [step, setStep] = useState<'form' | 'otp' | 'reset-code' | 'reset-password'>('form');
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [devCode, setDevCode] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState('');

  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'CLIENT' | 'HOST'>('CLIENT');
  const [city, setCity] = useState('');
  const [code, setCode] = useState('');
  // Pre-filled from a partner link; still editable, and optional.
  const [affiliateCode, setAffiliateCode] = useState(() => getReferral());

  const handle = async (action: () => Promise<void>) => {
    setBusy(true);
    setFieldErrors({});
    try {
      await action();
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setFieldErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  const submitLogin = () =>
    handle(async () => {
      const response = await api.post<AuthResponse>('/auth/login', {
        ...identifierPayload(identifier),
        password,
      });
      signIn(response);
      toast(t('Bon retour, {name} !', { name: response.user.fullName.split(' ')[0] ?? '' }), 'ok');
      navigate(redirectTo, { replace: true });
    });

  const submitSignup = () =>
    handle(async () => {
      const response = await api.post<AuthResponse>('/auth/signup', {
        email: email.trim().toLowerCase(),
        password,
        fullName,
        role,
        // The language the site is in, so the code email and every later one arrive in it.
        locale: language,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(city ? { city } : {}),
        ...(affiliateCode.trim() ? { affiliateCode: affiliateCode.trim() } : {}),
      });
      clearReferral();
      signIn(response);
      setDevCode(response.devCode ?? null);
      setStep('otp');
    });

  const submitOtp = () =>
    handle(async () => {
      const response = await api.post<AuthResponse>('/auth/otp/verify', {
        email: email.trim().toLowerCase(),
        code,
      });
      signIn(response);
      toast(t("Email vérifié. Bienvenue sur Bedly."), 'ok');
      navigate(role === 'HOST' ? '/verification' : redirectTo, { replace: true });
    });

  const resendOtp = () =>
    handle(async () => {
      const response = await api.post<{ devCode?: string }>('/auth/otp/request', {
        email: email.trim().toLowerCase(),
        purpose: 'SIGNUP',
      });
      setDevCode(response.devCode ?? null);
      toast(t("Nouveau code envoyé."), 'ok');
    });

  const startReset = () =>
    handle(async () => {
      const response = await api.post<{ devCode?: string }>('/auth/password/forgot', {
        email: email.trim().toLowerCase(),
      });
      setDevCode(response.devCode ?? null);
      setStep('reset-code');
      // The same words whether or not the address has an account: anything
      // else tells a stranger who is registered.
      toast(t("Si cette adresse a un compte, un code vient d’être envoyé."));
    });

  const confirmResetCode = () =>
    handle(async () => {
      const response = await api.post<{ resetToken: string }>('/auth/password/confirm-code', {
        email: email.trim().toLowerCase(),
        code,
      });
      setResetToken(response.resetToken);
      setStep('reset-password');
    });

  const finishReset = () =>
    handle(async () => {
      await api.post('/auth/password/reset', { resetToken, newPassword });
      toast(t("Mot de passe modifié. Connectez-vous."), 'ok');
      navigate('/connexion', { replace: true });
    });

  const codeInput = (
    <TextInput
      value={code}
      onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="000000"
      style={{ letterSpacing: '0.4em', fontSize: 20, textAlign: 'center' }}
    />
  );

  return (
    <div className="page page--narrow">
      <div className="card card--pad stack">
        {step === 'form' && mode === 'login' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void submitLogin();
            }}
          >
            <div className="stack stack--tight">
              <h1 style={{ fontSize: 26 }}>{t("Bon retour !")}</h1>
              <p className="muted small">{t("Connectez-vous pour retrouver vos réservations.")}</p>
            </div>
            <Field
              label={t("Email ou téléphone")}
              error={fieldErrors.email ?? fieldErrors.phone}
            >
              <TextInput
                value={identifier}
                onChange={setIdentifier}
                type="text"
                inputMode="email"
                autoComplete="username"
                placeholder={t("vous@exemple.ma ou 06 12 34 56 78")}
                error={Boolean(fieldErrors.email ?? fieldErrors.phone)}
              />
            </Field>
            <Field label={t("Mot de passe")} error={fieldErrors.password}>
              <TextInput
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="current-password"
                error={Boolean(fieldErrors.password)}
              />
            </Field>
            <button type="submit" className="btn btn--block" disabled={busy || !identifier || !password}>
              {busy ? t("Connexion…") : t("Se connecter")}
            </button>
            <GoogleButton label="signin_with" redirectTo={redirectTo} />
            <div className="row row--between small">
              <Link to="/mot-de-passe">{t("Mot de passe oublié ?")}</Link>
              <Link to="/inscription">{t("Créer un compte")}</Link>
            </div>
          </form>
        ) : null}

        {step === 'form' && mode === 'signup' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void submitSignup();
            }}
          >
            <div className="stack stack--tight">
              <h1 style={{ fontSize: 26 }}>{t("Créer un compte")}</h1>
              <p className="muted small">
                {t("Nous vous enverrons un code par email. Vous pourrez ajouter l’autre rôle plus tard.")}
              </p>
            </div>
            <GoogleButton label="signup_with" redirectTo={redirectTo} divider="after" />
            <Field label={t("Que venez-vous faire sur Bedly ?")}>
              <Select
                value={role}
                onChange={(value) => setRole(value as 'CLIENT' | 'HOST')}
                options={[
                  { value: 'CLIENT', label: t("Je cherche un logement") },
                  { value: 'HOST', label: t("Je propose un logement") },
                ]}
              />
            </Field>
            <Field label={t("Nom complet")} error={fieldErrors.fullName}>
              <TextInput
                value={fullName}
                onChange={setFullName}
                autoComplete="name"
                placeholder={t("Salma El Amrani")}
                error={Boolean(fieldErrors.fullName)}
              />
            </Field>
            <Field
              label={t("Email")}
              hint={t("Un email universitaire en .ac.ma accélère la vérification étudiante.")}
              error={fieldErrors.email}
            >
              <TextInput
                value={email}
                onChange={setEmail}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t("vous@exemple.ma")}
                error={Boolean(fieldErrors.email)}
              />
            </Field>
            <Field
              label={t("Téléphone (optionnel)")}
              hint={t("Pour que l’hôte ou le locataire puisse vous joindre. Jamais affiché publiquement.")}
              error={fieldErrors.phone}
            >
              <TextInput
                value={phone}
                onChange={setPhone}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="06 12 34 56 78"
                error={Boolean(fieldErrors.phone)}
              />
            </Field>
            <Field
              label={t("Code affilié (optionnel)")}
              hint={t("Si un partenaire de Bedly vous a donné un code.")}
              error={fieldErrors.affiliateCode}
            >
              <TextInput
                value={affiliateCode}
                onChange={(value) => setAffiliateCode(value.toUpperCase())}
                placeholder="BEDLY2026"
                error={Boolean(fieldErrors.affiliateCode)}
              />
            </Field>
            <Field label={t("Ville")} error={fieldErrors.city}>
              <TextInput value={city} onChange={setCity} autoComplete="address-level2" placeholder={t("Rabat")} />
            </Field>
            <Field
              label={t("Mot de passe")}
              hint={t("8 caractères minimum, avec des lettres et des chiffres.")}
              error={fieldErrors.password}
            >
              <TextInput
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="new-password"
                error={Boolean(fieldErrors.password)}
              />
            </Field>
            <button
              type="submit"
              className="btn btn--block"
              disabled={busy || !email || !password || !fullName}
            >
              {busy ? t("Création…") : t("Créer mon compte")}
            </button>
            <p className="tiny muted center" style={{ margin: 0 }}>
              {t("En continuant vous acceptez les conditions d’utilisation et la politique de confidentialité de Bedly.")}
            </p>
            <div className="center small">
              {t("Déjà un compte ?")} <Link to="/connexion">{t("Se connecter")}</Link>
            </div>
          </form>
        ) : null}

        {step === 'otp' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (code.length === 6) void submitOtp();
            }}
          >
            <div className="stack stack--tight">
              <h1 style={{ fontSize: 24 }}>{t("Vérifiez votre email")}</h1>
              <p className="muted small">
                {t("Code à 6 chiffres envoyé à")} <strong>{email}</strong>{t(". Pensez à regarder dans les spams.")}
              </p>
            </div>
            {devCode ? (
              <Notice tone="warn">
                {t("Mode développement : le code est")} <strong>{devCode}</strong>{t(". En production, aucun code n’est jamais renvoyé par l’API.")}
              </Notice>
            ) : null}
            <Field label={t("Code de vérification")} error={fieldErrors.code}>
              {codeInput}
            </Field>
            <button type="submit" className="btn btn--block" disabled={busy || code.length !== 6}>
              {busy ? t("Vérification…") : t("Vérifier mon email")}
            </button>
            <div className="row row--between small">
              <button type="button" className="btn btn--quiet btn--sm" onClick={resendOtp} disabled={busy}>
                {t("Renvoyer le code")}
              </button>
              <Link to="/recherche">{t("Plus tard")}</Link>
            </div>
          </form>
        ) : null}

        {step === 'form' && mode === 'reset' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void startReset();
            }}
          >
            <div className="stack stack--tight">
              <h1 style={{ fontSize: 24 }}>{t("Mot de passe oublié")}</h1>
              <p className="muted small">{t("Nous envoyons un code à l’adresse email de votre compte.")}</p>
            </div>
            <Field label={t("Email")} error={fieldErrors.email}>
              <TextInput
                value={email}
                onChange={setEmail}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t("vous@exemple.ma")}
              />
            </Field>
            <button type="submit" className="btn btn--block" disabled={busy || !email}>
              {busy ? t("Envoi…") : t("Envoyer le code")}
            </button>
            <div className="center small">
              <Link to="/connexion">{t("Retour à la connexion")}</Link>
            </div>
          </form>
        ) : null}

        {step === 'reset-code' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (code.length === 6) void confirmResetCode();
            }}
          >
            <h1 style={{ fontSize: 24 }}>{t("Entrez le code reçu")}</h1>
            <p className="muted small" style={{ margin: 0 }}>
              {t("Envoyé à")} <strong>{email}</strong>{t(", s’il correspond à un compte.")}
            </p>
            {devCode ? (
              <Notice tone="warn">
                {t("Mode développement : le code est")} <strong>{devCode}</strong>.
              </Notice>
            ) : null}
            <Field label={t("Code à 6 chiffres")} error={fieldErrors.code}>
              {codeInput}
            </Field>
            <button type="submit" className="btn btn--block" disabled={busy || code.length !== 6}>
              {busy ? t("Vérification…") : t("Continuer")}
            </button>
          </form>
        ) : null}

        {step === 'reset-password' ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void finishReset();
            }}
          >
            <h1 style={{ fontSize: 24 }}>{t("Nouveau mot de passe")}</h1>
            <Notice tone="info">
              {t("Changer le mot de passe déconnecte tous vos appareils. C’est voulu.")}
            </Notice>
            <Field label={t("Nouveau mot de passe")} error={fieldErrors.newPassword}>
              <TextInput
                value={newPassword}
                onChange={setNewPassword}
                type="password"
                autoComplete="new-password"
              />
            </Field>
            <button type="submit" className="btn btn--block" disabled={busy || !newPassword}>
              {busy ? t("Enregistrement…") : t("Enregistrer")}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
